import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import StatsGrid from '@/components/StatsGrid';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import DialogFormFooter from '@/components/DialogFormFooter';
import api from '@/lib/api';
import { toast } from 'sonner';
import { GitBranch, Plus, Play, CheckCircle, XCircle, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const ACTION_TYPES = [
  { value: 'admin_review', label: 'Admin review (wait for approval)' },
  { value: 'notification', label: 'Send notification' },
];

const emptyForm = { name: '', description: '', trigger_form_id: '', steps: [] };
const newStep = () => ({
  id: uuidv4(), name: '', description: '',
  action: { action_type: 'admin_review', notification_recipients: [] },
});

const Workflows = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [instances, setInstances] = useState([]);
  const [formTemplates, setFormTemplates] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [wfRes, instRes, formsRes] = await Promise.all([
        api.get('/workflows'),
        api.get('/workflow-instances'),
        api.get('/form-templates').catch(() => ({ data: [] })),
      ]);
      setWorkflows(wfRes.data);
      setInstances(instRes.data);
      setFormTemplates(formsRes.data);
    } catch (err) {
      toast.error('Failed to load workflows');
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (user.role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/');
        return;
      }
      fetchData();
    }
  }, [user, loading, navigate, fetchData]);

  const formName = (id) => formTemplates.find((f) => f.id === id)?.name || '—';

  // --- steps editor ---
  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, newStep()] }));
  const removeStep = (id) => setForm((f) => ({ ...f, steps: f.steps.filter((s) => s.id !== id) }));
  const updateStep = (id, field, value) =>
    setForm((f) => ({ ...f, steps: f.steps.map((s) => (s.id === id ? { ...s, [field]: value } : s)) }));
  const updateAction = (id, field, value) =>
    setForm((f) => ({
      ...f,
      steps: f.steps.map((s) => (s.id === id ? { ...s, action: { ...s.action, [field]: value } } : s)),
    }));

  const createWorkflow = async () => {
    setSubmitting(true);
    try {
      const payload = {
        id: uuidv4(),
        name: form.name,
        description: form.description,
        trigger_form_id: form.trigger_form_id || null,
        status: 'active',
        steps: form.steps.map((s, i) => ({ ...s, order: i })),
      };
      await api.post('/workflows', payload);
      toast.success('Workflow created');
      setShowDialog(false);
      setForm(emptyForm);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create workflow');
    } finally {
      setSubmitting(false);
    }
  };

  const advance = async (instance, action) => {
    try {
      await api.post(`/workflow-instances/${instance.id}/advance`, { action, notes: '' });
      toast.success(action === 'approve' ? 'Step approved' : 'Workflow rejected');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to advance workflow');
    }
  };

  const activeInstances = instances.filter((i) => i.status === 'in_progress');

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title="Workflows"
        icon={GitBranch}
        titleTestId="workflows-title"
        actions={
          <Button onClick={() => { setForm(emptyForm); setShowDialog(true); }} data-testid="new-workflow-btn">
            <Plus size={20} /> New Workflow
          </Button>
        }
      />

      <StatsGrid cols={4}>
        <StatCard icon={GitBranch} iconClassName="text-primary" value={workflows.length} label="Workflows" />
        <StatCard icon={Play} iconClassName="text-yellow-500" valueClassName="text-yellow-600" value={activeInstances.length} label="Active" />
        <StatCard icon={CheckCircle} iconClassName="text-green-500" valueClassName="text-green-600" value={instances.filter((i) => i.status === 'completed').length} label="Completed" />
        <StatCard icon={XCircle} iconClassName="text-red-500" valueClassName="text-red-600" value={instances.filter((i) => i.status === 'rejected').length} label="Rejected" />
      </StatsGrid>

      {/* Active workflow instances */}
      <h2 className="font-serif text-xl font-semibold text-primary mb-3">Active Workflows</h2>
      {activeInstances.length === 0 ? (
        <EmptyState icon={Play} message="No active workflows. They start automatically when a triggering form is submitted." />
      ) : (
        <div className="space-y-3 mb-8">
          {activeInstances.map((inst) => (
            <Card key={inst.id} data-testid={`instance-${inst.id}`}>
              <CardContent className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <p className="font-medium">{inst.workflow_name}</p>
                  <p className="text-sm text-muted-foreground">
                    Step {(inst.current_step_index ?? 0) + 1}
                    {inst.started_at && ` · started ${new Date(inst.started_at).toLocaleString()}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => advance(inst, 'approve')} data-testid={`approve-${inst.id}`}>
                    <CheckCircle size={16} /> Approve
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => advance(inst, 'reject')} data-testid={`reject-${inst.id}`}>
                    <XCircle size={16} /> Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Workflow templates */}
      <h2 className="font-serif text-xl font-semibold text-primary mb-3">Workflow Templates</h2>
      {workflows.length === 0 ? (
        <EmptyState icon={GitBranch} message="No workflows yet. Create one to automate what happens after a form is submitted." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="workflow-templates">
          {workflows.map((wf) => (
            <Card key={wf.id} data-testid={`workflow-${wf.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-serif text-lg font-semibold text-primary">{wf.name}</h3>
                  <Badge className={wf.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{wf.status}</Badge>
                </div>
                {wf.description && <p className="text-sm text-muted-foreground mb-2">{wf.description}</p>}
                <p className="text-xs text-muted-foreground">Trigger form: <span className="font-medium">{formName(wf.trigger_form_id)}</span></p>
                <ol className="mt-2 space-y-1 text-sm list-decimal list-inside">
                  {(wf.steps || []).map((s) => (
                    <li key={s.id}>
                      {s.name} <span className="text-xs text-muted-foreground">({s.action?.action_type === 'notification' ? 'notify' : 'admin review'})</span>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-bold text-primary">New Workflow</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="wf-name">Name *</Label>
              <Input id="wf-name" data-testid="wf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="wf-description">Description</Label>
              <Textarea id="wf-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="wf-trigger-form">Trigger form</Label>
              <select
                id="wf-trigger-form"
                data-testid="wf-trigger-form"
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={form.trigger_form_id}
                onChange={(e) => setForm({ ...form, trigger_form_id: e.target.value })}
              >
                <option value="">— Select a form —</option>
                {formTemplates.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium">Steps</h4>
                <Button type="button" size="sm" variant="outline" onClick={addStep} data-testid="add-step-btn">
                  <Plus size={14} /> Add step
                </Button>
              </div>
              {form.steps.length === 0 ? (
                <p className="text-sm text-muted-foreground">No steps yet.</p>
              ) : (
                <div className="space-y-2">
                  {form.steps.map((s, i) => (
                    <div key={s.id} className="border rounded-md p-3 space-y-2" data-testid={`step-row-${s.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">Step {i + 1}</span>
                        <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => removeStep(s.id)} aria-label="Remove step">
                          <X size={16} />
                        </button>
                      </div>
                      <Input placeholder="Step name" value={s.name} onChange={(e) => updateStep(s.id, 'name', e.target.value)} aria-label="Step name" />
                      <Input placeholder="Description" value={s.description} onChange={(e) => updateStep(s.id, 'description', e.target.value)} aria-label="Step description" />
                      <select
                        className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        value={s.action.action_type}
                        onChange={(e) => updateAction(s.id, 'action_type', e.target.value)}
                        aria-label="Step action type"
                      >
                        {ACTION_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                      </select>
                      {s.action.action_type === 'notification' && (
                        <Input
                          placeholder="Recipient emails (comma-separated)"
                          value={(s.action.notification_recipients || []).join(', ')}
                          onChange={(e) => updateAction(s.id, 'notification_recipients', e.target.value.split(',').map((v) => v.trim()).filter(Boolean))}
                          aria-label="Notification recipients"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFormFooter
              onCancel={() => setShowDialog(false)}
              onSubmit={createWorkflow}
              submitLabel="Create Workflow"
              disabled={!form.name || submitting}
              submitTestId="save-workflow-btn"
            />
          </div>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};

export default Workflows;
